/**
 * Arithmetic - Subtraction
 *
 * @module twofloat/arithmetic/sub
 */

import { normalize, twoDiff } from '../base/eft.js';
import { DWMinusFP, AccurateDWMinusDW } from '../base/algorithms.js';
import type { f64, TwoF64 } from '../base/common.js';

export const sub11 = twoDiff;
export const sub21 = DWMinusFP;
export const sub22 = AccurateDWMinusDW;

/**
 * Extended-precision subtraction `x - y`.
 *
 * Relative error bound: `2u²` with `u = 2^-53`
 *
 * FP ops: 10
 *
 * @param x A `f64` number
 * @param y A `TwoF64` number
 * @returns The {@link TwoF64|`TwoF64`} representation of `x - y`
 */
export function sub12(x: f64, y: TwoF64): TwoF64;
export function sub12(x: f64, [yhi, ylo]: TwoF64): TwoF64 {
  const [hi, lo] = sub11(x, yhi);
  return normalize(hi, lo - ylo);
}

/**
 * Extended-precision subtraction `x - y`.
 *
 * @param x A `f64` or `TwoF64` number
 * @param y A `f64` or `TwoF64` number
 * @returns The {@link TwoF64|`TwoF64`} representation of `x - y`
 */
export function sub(x: f64 | TwoF64, y: f64 | TwoF64): TwoF64 {
  return typeof x === 'number'
    ? (typeof y === 'number' ? sub11(x, y) : sub12(x, y))
    : (typeof y === 'number' ? sub21(x, y) : sub22(x, y));
}
